import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePostUserNarrativePreferences } from '@/api/generated/user/user';
import narrativesBgImage from '@/assets/icons/Narratives.svg';
import { Spinner } from '@/components/ui/spinner';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import {
  CustomNarrativeRequestCard,
  PreviousRequestsList,
} from '@/features/narrative/components/CustomNarrativeRequestCard';
import { NarrativeCardsList } from '@/features/narrative/components/NarrativeCardsList';
import { NarrativeEmptyState } from '@/features/narrative/components/NarrativeEmptyState';
import { NarrativeHeader } from '@/features/narrative/components/NarrativeHeader';
import { NarrativeOnItDialog } from '@/features/narrative/components/NarrativeOnItDialog';
import { NarrativeTabs } from '@/features/narrative/components/NarrativeTabs';
import { NarrativeUploadDialog } from '@/features/narrative/components/NarrativeUploadDialog';
import { NarrativeWalkthroughDialog } from '@/features/narrative/components/NarrativeWalkthroughDialog';
import { useCustomNarratives } from '@/features/narrative/hooks/useCustomNarratives';
import { useNarrativeFileUpload } from '@/features/narrative/hooks/useNarrativeFileUpload';
import { useNarrativeMutations } from '@/features/narrative/hooks/useNarrativeMutations';
import { useNarrativeWalkthrough } from '@/features/narrative/hooks/useNarrativeWalkthrough';
import {
  type RollingNarrativeKind,
  useRollingNarratives,
} from '@/features/narrative/hooks/useRollingNarratives';
import { useNarrativeStore } from '@/features/narrative/stores/useNarrativeStore';
import { narrativeToCardData } from '@/features/narrative/utils/narrativeCardMapper';
import { DATE_RANGE_OPTIONS, type DateRangeFilter, getNarrativeType } from '@/utils/constants';
import { convertDateFormat, formatDateDisplay, formatDateWithTime } from '@/utils/dateUtils';
import { useIntersectionObserver } from '@/utils/hooks/useIntersectionObserver';
import { cn } from '@/utils/utils';

export function NarrativePage() {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { activeFilter, setActiveFilter } = useNarrativeStore();
  const walkthrough = useNarrativeWalkthrough();
  const narrativePreferencesMutation = usePostUserNarrativePreferences({
    mutation: { retry: false },
  });

  const currentKind = getNarrativeType(activeFilter);
  const isWalkthroughDone = walkthrough.phase === 'done';
  const isRolling = currentKind === 'TODAY' || currentKind === 'WEEK';

  const mutations = useNarrativeMutations({
    onDeleteError: (err) => toast.error(t('narrative.deleteFailed', { message: err.message })),
    onDeleteSuccess: () => toast.success(t('narrative.deleteSuccess')),
    onUpdateError: () => toast.error(t('narrative.updateFailed')),
    onUpdateSuccess: () => toast.success(t('narrative.updateSuccess')),
  });

  const rolling = useRollingNarratives({
    enabled: isWalkthroughDone && isRolling,
    // kind is unused while disabled; default to TODAY so the queryKey stays stable.
    kind: isRolling ? (currentKind as RollingNarrativeKind) : 'TODAY',
  });

  const custom = useCustomNarratives({
    enabled: isWalkthroughDone && activeFilter === 'custom',
    onJobSettled: (job) => {
      if (job.status === 'COMPLETED') {
        posthog.capture(ANALYTICS_EVENTS.CUSTOM_NARRATIVE_REPORTS_GENERATED, {
          customNarrativeReportCount: job.count ?? 0,
          jobId: job.id,
        });
      }
    },
    onTriggerError: () => toast.error(t('narrative.generateFailed')),
  });

  const handleUpdateNarrative = (id: string, content: string) => {
    mutations.update(
      { id, summary: content },
      {
        onSuccess: () => {
          posthog.capture(ANALYTICS_EVENTS.NARRATIVE_EDITED, {
            activeFilter,
            isRequestedView: custom.selectedJobId !== null,
            narrativeEditCount: 1,
            narrativeId: id,
          });
        },
      },
    );
  };

  const handleDeleteNarrative = (id: string) => {
    mutations.delete(id);
  };

  const narratives = rolling.narratives.map((n) => narrativeToCardData(n, t));
  const requestedNarratives = custom.selectedNarratives.map((n) => narrativeToCardData(n, t));

  const previousCustomRequests = custom.jobs.map((job) => ({
    count: job.count,
    countLabel: job.count?.toString() || '0',
    fromLabel: job.startDate ? formatDateDisplay(job.startDate) : '',
    id: job.id,
    isFailed: job.status === 'FAILED',
    isLoading: job.status !== 'COMPLETED' && job.status !== 'FAILED',
    jobId: job.id,
    subtitle: job.createdAt ? formatDateWithTime(job.createdAt) : '',
    toLabel: job.endDate ? formatDateDisplay(job.endDate) : '',
  }));

  const showRequestedDialog = custom.selectedJobId !== null;
  const showSkeletonLoader = rolling.isLoading;
  const isBgGenerating = isRolling && rolling.isGenerating;

  const [showOnItDialog, setShowOnItDialog] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  const requestedScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    fileInputRef,
    handleCancelFile,
    handleClose,
    handleFileInputChange,
    handleRetry,
    handleRetryFile,
    handleUploadClick,
    setShowUploadDialog,
    showUploadDialog,
    uploadFiles,
  } = useNarrativeFileUpload({
    markWalkthroughUploaded: walkthrough.markUploaded,
    narrativePreferencesMutation,
    setActiveFilter,
  });

  // Local UI suppression: when the user clicks Upload from `upload_prompt`,
  // hide the dialog while the OS file picker is open. Auto-clears once the
  // walkthrough completes (markUploaded → phase becomes 'done').
  const [walkthroughDismissed, setWalkthroughDismissed] = useState(false);
  useEffect(() => {
    if (isWalkthroughDone) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- clears transient suppression once the walkthrough completes
      setWalkthroughDismissed(false);
    }
  }, [isWalkthroughDone]);

  const showIntro = !walkthrough.isLoading && !isWalkthroughDone;
  const showWalkthrough = showIntro && !walkthroughDismissed;
  const walkthroughStepNumber: 1 | 2 | 3 =
    walkthrough.phase === 'intro' ? 1 : walkthrough.phase === 'upload_prompt' ? 2 : 3;

  const handleNextClick = () => walkthrough.advance();
  const handleSetUpLater = () => walkthrough.skip();
  const handleDone = () => {
    walkthrough.acknowledge();
    setShowUploadDialog(false);
    setActiveFilter('today');
  };
  const handleUploadNarrativesClick = () => {
    setWalkthroughDismissed(true);
    handleUploadClick();
  };

  // Tab-change analytics with current count. Refetch is explicit so we read
  // the freshest pagination total (the rolling hook also auto-refetches on
  // job completion; React Query dedupes concurrent refetches).
  const { refetch: refetchRolling } = rolling;
  useEffect(() => {
    if (!isWalkthroughDone || !isRolling) {
      return;
    }
    let isCurrent = true;
    void refetchRolling().then((result) => {
      if (!isCurrent || result.status !== 'success') {
        return;
      }
      const count =
        result.data?.pages.reduce((maxTotal, page) => {
          const total = page.pagination?.total ?? 0;
          return total > maxTotal ? total : maxTotal;
        }, 0) ?? 0;
      const event =
        currentKind === 'TODAY'
          ? ANALYTICS_EVENTS.TODAY_NARRATIVE_GENERATED
          : ANALYTICS_EVENTS.WEEK_NARRATIVE_GENERATED;
      posthog.capture(event, { narrativeCount: count });
    });
    return () => {
      isCurrent = false;
    };
  }, [currentKind, isWalkthroughDone, isRolling, refetchRolling, posthog]);

  const handlePreviousRequestClick = (request: { isLoading: boolean; jobId: string | null }) => {
    if (request.isLoading || !request.jobId) {
      return;
    }
    custom.select(request.jobId);
  };

  const { ref: requestedObserverRef } = useIntersectionObserver(
    () => {
      if (
        showRequestedDialog &&
        custom.hasNextPage &&
        !custom.isFetchingNextPage &&
        !custom.isLoadingSelected
      ) {
        void custom.fetchNextPage();
      }
    },
    {
      enabled:
        showRequestedDialog &&
        custom.hasNextPage &&
        !custom.isFetchingNextPage &&
        !custom.isLoadingSelected,
      root: requestedScrollRef,
      rootMargin: '100px',
      threshold: 0.1,
    },
  );

  const handleGenerateCustomNarratives = async () => {
    setShowOnItDialog(true);
    try {
      await custom.requestGeneration({
        endDate: convertDateFormat(customEndDate),
        startDate: convertDateFormat(customStartDate),
      });
      setCustomStartDate('');
      setCustomEndDate('');
    } catch {
      // Surface handled via onTriggerError.
    }
  };

  return (
    <div
      className={cn(
        'flex h-full flex-1 flex-col overflow-hidden',
        showIntro ? 'profile-gradient-bg' : 'bg-back-bg2',
      )}
    >
      <div className="max-w-page mx-auto flex h-full w-full flex-col overflow-hidden">
        {showWalkthrough && (
          <div aria-hidden="true" className="fixed inset-0 bottom-14 z-50 bg-black/50" />
        )}

        {showWalkthrough && (
          <div className="pointer-events-none fixed inset-0 z-60 flex items-center justify-center">
            <div className="pointer-events-auto">
              <NarrativeWalkthroughDialog
                onDone={handleDone}
                onNext={handleNextClick}
                onOpenChange={(open) => {
                  if (!open) {
                    setWalkthroughDismissed(true);
                  }
                }}
                onSetUpLater={handleSetUpLater}
                onUpload={handleUploadNarrativesClick}
                open={showWalkthrough}
                step={walkthroughStepNumber}
              />
            </div>
          </div>
        )}

        {activeFilter === 'custom' && showRequestedDialog ? (
          <NarrativeHeader
            isRequestedView
            onBack={() => custom.back()}
            totalItems={requestedNarratives.length}
          />
        ) : (
          <NarrativeHeader isRequestedView={false} totalItems={narratives.length} />
        )}

        <div className="relative shrink-0">
          <NarrativeTabs
            onChange={(value) => {
              setActiveFilter(value as DateRangeFilter);
            }}
            options={DATE_RANGE_OPTIONS}
            value={activeFilter}
          />
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          {showIntro && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <img
                alt="Narratives"
                className="h-auto w-full max-w-100 object-contain"
                src={narrativesBgImage}
              />
            </div>
          )}

          <div
            className={cn(
              'relative z-10 flex min-h-0 flex-1 flex-col',
              (showWalkthrough || showUploadDialog) && 'blur-[3px]',
            )}
          >
            {activeFilter === 'custom' ? (
              showRequestedDialog ? (
                <div
                  className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-3 pt-4 pb-4"
                  ref={requestedScrollRef}
                >
                  <NarrativeCardsList
                    filterType="custom"
                    isLoading={custom.isLoadingSelected && requestedNarratives.length === 0}
                    narratives={requestedNarratives}
                    onDelete={handleDeleteNarrative}
                    onSave={handleUpdateNarrative}
                  />
                  {custom.hasNextPage && (
                    <div
                      className="flex h-10 w-full items-center justify-center"
                      ref={requestedObserverRef}
                    >
                      {custom.isFetchingNextPage ? (
                        <Spinner className="text-neutral-mid size-4" />
                      ) : null}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full min-h-0 flex-col">
                  <div className="shrink-0 px-3 pt-4 pb-3">
                    <CustomNarrativeRequestCard
                      endDate={customEndDate}
                      onEndDateChange={setCustomEndDate}
                      onGenerateClick={handleGenerateCustomNarratives}
                      onStartDateChange={setCustomStartDate}
                      startDate={customStartDate}
                    />
                  </div>
                  <div className="relative min-h-0 flex-1 overflow-y-auto px-3 pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {previousCustomRequests.length > 0 ? (
                      <PreviousRequestsList
                        onRequestClick={handlePreviousRequestClick}
                        requests={previousCustomRequests}
                      />
                    ) : (
                      <NarrativeEmptyState filterType="custom" />
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className={cn('custom-scrollbar min-h-0 flex-1 overflow-y-auto px-3 pt-4 pb-4')}>
                {showIntro ? null : (
                  <>
                    {isBgGenerating && !showSkeletonLoader && (
                      <div className="bg-card mb-3 flex items-center justify-center gap-2 rounded-lg px-3 py-2 shadow-[0_1px_5px_0_#71778B1A]">
                        <Spinner className="text-neutral-mid size-4" />
                        <span className="text-secondary font-sans text-[13px] leading-[170%]">
                          {t('narrative.generatingMore')}
                        </span>
                      </div>
                    )}
                    {(showSkeletonLoader || !isBgGenerating || narratives.length > 0) && (
                      <NarrativeCardsList
                        filterType={activeFilter}
                        isLoading={showSkeletonLoader}
                        narratives={showSkeletonLoader ? [] : narratives}
                        onDelete={handleDeleteNarrative}
                        onSave={handleUpdateNarrative}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <input
        accept=".pdf,.doc,.docx"
        aria-label={t('narrative.uploadAria')}
        className="hidden"
        multiple
        onChange={handleFileInputChange}
        ref={fileInputRef}
        type="file"
      />

      <NarrativeUploadDialog
        files={uploadFiles}
        onCancelFile={handleCancelFile}
        onClose={handleClose}
        onOpenChange={setShowUploadDialog}
        onRetry={handleRetry}
        onRetryFile={handleRetryFile}
        open={showUploadDialog}
      />

      <NarrativeOnItDialog onOpenChange={setShowOnItDialog} open={showOnItDialog} />
    </div>
  );
}
